/**
 * Lessor create/edit dialog. Reused from:
 *   - V2 lessor list page (Add / Edit)
 *   - V2 add-property wizard Step 2a (Create new lessor inline)
 *
 * Validation: zod + react-hook-form, mode='onBlur' so users see errors as
 * they tab through fields, not only on submit. Required fields are
 * name + type + rep_name + bank_holder + bank_iban.
 *
 * Representor model: single string. Contracts say "[Lessor], vertreten
 * durch [Representor]" and the second slot is whatever string the
 * editor types — usually a role like "den Geschäftsführer", sometimes
 * an entity name like "CoLiviq GmbH" in a management deal. We used to
 * store this as Name + Role separately, but that didn't match how it
 * gets rendered. The DB still has a `rep_role` column for back-compat
 * with V1 admin rows; this form always writes it as null.
 *
 * UX:
 *   - Auto-focus the first text field when the dialog opens
 *   - Sections rendered as bordered card-style groups for clear hierarchy
 *   - IBAN / BIC use font-mono + uppercase autocomplete hints
 *   - ⌘+Enter / Ctrl+Enter submits from anywhere in the form
 *   - Esc closes (Dialog default)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, Pencil } from "lucide-react";
import { LESSOR_TYPES, type Lessor, type LessorInput } from "@/types/lessor";
import { removesBankDetails } from "@/hooks/v2/useLessors";
import { useLanguage } from "@/contexts/LanguageContext";
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete";

/** Builds the zod schema with translated validation messages. Threaded through
 *  `t` since this runs outside any component's render (called once, memoized,
 *  inside LessorFormDialog with the current `t`). */
function buildLessorSchema(t: ReturnType<typeof useLanguage>["t"]) {
  return z.object({
    name: z
      .string()
      .trim()
      .min(1, t("admin.lessor.validation.required", "Required")),
    type: z.enum(LESSOR_TYPES),
    address: z.string().nullable().optional(),
    city_postal: z.string().nullable().optional(),
    email: z
      .string()
      .nullable()
      .optional()
      .refine(
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        t(
          "admin.lessor.validation.invalidEmail",
          "Enter a valid email or leave blank"
        )
      ),
    phone: z.string().nullable().optional(),
    rep_name: z
      .string()
      .trim()
      .min(1, t("admin.lessor.validation.required", "Required")),
    rep_note: z.string().nullable().optional(),
    bank_holder: z
      .string()
      .trim()
      .min(1, t("admin.lessor.validation.required", "Required")),
    bank_iban: z
      .string()
      .trim()
      .min(
        15,
        t("admin.lessor.validation.ibanTooShort", "IBAN looks too short")
      )
      .max(34, t("admin.lessor.validation.ibanTooLong", "IBAN looks too long")),
    bank_bic: z.string().nullable().optional(),
    bank_name: z.string().nullable().optional(),
    is_platform: z.boolean(),
  });
}

type FormValues = z.infer<ReturnType<typeof buildLessorSchema>>;

const EMPTY_VALUES: FormValues = {
  name: "",
  type: "Company",
  address: "",
  city_postal: "",
  email: "",
  phone: "",
  rep_name: "",
  rep_note: "",
  bank_holder: "",
  bank_iban: "",
  bank_bic: "",
  bank_name: "",
  is_platform: false,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  lessor?: Lessor | null;
  isSubmitting?: boolean;
  onSubmit: (input: LessorInput, reason?: string) => void;
};

export function LessorFormDialog({
  open,
  onOpenChange,
  mode,
  lessor,
  isSubmitting,
  onSubmit,
}: Props) {
  const { t } = useLanguage();
  const lessorSchema = useMemo(() => buildLessorSchema(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(lessorSchema),
    defaultValues: EMPTY_VALUES,
    mode: "onBlur",
  });
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  // Pending save that would CLEAR a bank_bic/bank_name the owner has today,
  // held until a reason is given. The database refuses that write without one
  // (20260817110100_bank_detail_removal_needs_a_reason.sql) — asking here
  // means the admin is prompted before the save, not left staring at a raw
  // database error after already typing the rest of their edit.
  const [bankReason, setBankReason] = useState<{
    input: LessorInput;
    text: string;
  } | null>(null);

  // Reset form when entering edit mode (or switching between rows). Auto-focus
  // the first field once the dialog has rendered.
  useEffect(() => {
    if (open) {
      if (mode === "edit" && lessor) {
        form.reset({
          name: lessor.name,
          type: lessor.type,
          address: lessor.address ?? "",
          city_postal: lessor.city_postal ?? "",
          email: lessor.email ?? "",
          phone: lessor.phone ?? "",
          rep_name: lessor.rep_name ?? "",
          rep_note: lessor.rep_note ?? "",
          bank_holder: lessor.bank_holder ?? "",
          bank_iban: lessor.bank_iban ?? "",
          bank_bic: lessor.bank_bic ?? "",
          bank_name: lessor.bank_name ?? "",
          is_platform: lessor.is_platform ?? false,
        });
      } else {
        form.reset(EMPTY_VALUES);
      }
      setBankReason(null);
      // Defer focus to the next tick so dialog has mounted
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    }
  }, [open, mode, lessor, form]);

  const handleSubmit = form.handleSubmit((values) => {
    // Coerce empty optional strings to null so the DB stores NULL instead of ""
    const nullify = (v: string | null | undefined) => {
      const trimmed = (v ?? "").trim();
      return trimmed === "" ? null : trimmed;
    };
    const input: LessorInput = {
      name: values.name.trim(),
      type: values.type,
      address: nullify(values.address),
      city_postal: nullify(values.city_postal),
      email: nullify(values.email),
      phone: nullify(values.phone),
      rep_name: values.rep_name.trim(),
      // Always null — the form no longer collects role separately. Existing
      // V1 rows that still have a role keep it until an editor saves the
      // lessor here; saving clears the legacy field. See file header.
      rep_role: null,
      rep_note: nullify(values.rep_note),
      bank_holder: values.bank_holder.trim(),
      bank_iban: values.bank_iban.trim().toUpperCase(),
      bank_bic: nullify(values.bank_bic)?.toUpperCase() ?? null,
      bank_name: nullify(values.bank_name),
      is_platform: values.is_platform,
    };
    // Only an edit has a "before" to clear FROM. Ask for the reason the
    // database will require rather than let the admin discover the refusal
    // after they have already saved.
    if (mode === "edit" && removesBankDetails(lessor, input)) {
      setBankReason({ input, text: "" });
      return;
    }
    onSubmit(input);
  });

  // ⌘+Enter / Ctrl+Enter submits from anywhere in the form
  function handleFormKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  const errs = form.formState.errors;
  const reg = form.register;

  // Tailwind helper: red ring on invalid fields
  const fieldClass = (hasError: boolean) =>
    hasError ? "border-destructive focus-visible:ring-destructive" : "";

  // Spread of register() that captures the "name" field's ref so we can
  // auto-focus on open. react-hook-form's register returns a ref callback;
  // we forward to its callback AND store our own.
  const nameReg = reg("name");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="space-y-0 border-b bg-muted/30 px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                {mode === "create" ? (
                  <Building2 className="h-5 w-5" />
                ) : (
                  <Pencil className="h-5 w-5" />
                )}
              </span>
              <div className="min-w-0">
                <DialogTitle className="text-lg">
                  {mode === "create"
                    ? t("admin.owner.add", "Add an owner")
                    : t("admin.owner.edit", "Edit owner")}
                </DialogTitle>
                <DialogDescription className="mt-0.5">
                  {t(
                    "admin.lessor.formDesc",
                    "Property owner record. Default representor and bank account flow into linked properties unless an override is set."
                  )}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            onKeyDown={handleFormKeyDown}
            className="space-y-4 px-6 py-5"
          >
            {/* ─── Company section ─── */}
            <Section
              title={t("admin.section.company", "Company")}
              description={t(
                "admin.section.companyDesc",
                "Legal entity that owns the property."
              )}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t("admin.field.type", "Type")} required>
                  <Select
                    value={form.watch("type")}
                    onValueChange={(v) =>
                      form.setValue("type", v as FormValues["type"])
                    }
                  >
                    <SelectTrigger id="lessor-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LESSOR_TYPES.map((lt) => (
                        <SelectItem key={lt} value={lt}>
                          {lt === "GbR"
                            ? "GbR"
                            : t(`admin.lessor.type.${lt.toLowerCase()}`, lt)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  label={t("admin.field.name", "Name")}
                  required
                  error={errs.name?.message}
                >
                  <Input
                    id="lessor-name"
                    placeholder="e.g. Lehel Immobilien GmbH"
                    className={fieldClass(!!errs.name)}
                    {...nameReg}
                    ref={(el) => {
                      nameReg.ref(el);
                      firstFieldRef.current = el;
                    }}
                  />
                </Field>

                <Field
                  label={t("admin.address", "Address")}
                  className="sm:col-span-2"
                >
                  <AddressAutocomplete
                    id="lessor-address"
                    placeholder="Strasse + Hausnummer"
                    value={form.watch("address") ?? ""}
                    onChange={(v) =>
                      form.setValue("address", v, { shouldDirty: true })
                    }
                    onSelect={({ street, houseNumber, postalCode, city }) => {
                      form.setValue(
                        "address",
                        houseNumber ? `${street} ${houseNumber}` : street,
                        { shouldDirty: true }
                      );
                      if (postalCode || city) {
                        form.setValue(
                          "city_postal",
                          [postalCode, city].filter(Boolean).join(" "),
                          { shouldDirty: true }
                        );
                      }
                    }}
                  />
                </Field>

                <Field label={t("admin.field.cityPostal", "City / Postal")}>
                  <Input
                    id="lessor-city"
                    placeholder="80331 München"
                    {...reg("city_postal")}
                  />
                </Field>

                <Field
                  label={t("admin.field.email", "Email")}
                  error={errs.email?.message}
                >
                  <Input
                    id="lessor-email"
                    type="email"
                    inputMode="email"
                    placeholder="contact@…"
                    className={fieldClass(!!errs.email)}
                    {...reg("email")}
                  />
                </Field>

                <Field
                  label={t("admin.field.phone", "Phone")}
                  className="sm:col-span-2"
                >
                  <Input
                    id="lessor-phone"
                    type="tel"
                    inputMode="tel"
                    placeholder="+49 …"
                    {...reg("phone")}
                  />
                </Field>
              </div>
            </Section>

            {/* ─── Representor section ─── */}
            <Section
              title={t("admin.section.representor", "Default representor")}
              description={t(
                "admin.section.representorDesc",
                'Goes after "vertreten durch" on contracts. Usually a role like "den Geschäftsführer", or an entity name like "Coliviq GmbH" in a management deal. Can be overridden per property.'
              )}
            >
              <div className="grid grid-cols-1 gap-3">
                <Field
                  label={t("admin.field.representor", "Representor")}
                  required
                  error={errs.rep_name?.message}
                >
                  <Input
                    id="rep-name"
                    placeholder='e.g. "den Geschäftsführer" or "Coliviq GmbH"'
                    className={fieldClass(!!errs.rep_name)}
                    {...reg("rep_name")}
                  />
                </Field>

                <Field
                  label={t("admin.field.note", "Note")}
                  hint={t(
                    "admin.field.noteHint",
                    "Optional. Shows under the representor on contracts."
                  )}
                >
                  <Textarea
                    id="rep-note"
                    placeholder="e.g. vertreten durch Coliviq GmbH"
                    rows={2}
                    {...reg("rep_note")}
                  />
                </Field>
              </div>
            </Section>

            {/* ─── Bank section ─── */}
            <Section
              title={t("admin.section.bankAccount", "Default bank account")}
              description={t(
                "admin.section.bankAccountDesc",
                "Where rent for properties owned by this owner is sent. Visible to admins only."
              )}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label={t("admin.field.bankHolder", "Account holder")}
                  required
                  error={errs.bank_holder?.message}
                >
                  <Input
                    id="bank-holder"
                    className={fieldClass(!!errs.bank_holder)}
                    {...reg("bank_holder")}
                  />
                </Field>

                <Field
                  label={t("admin.field.bankIban", "IBAN")}
                  required
                  error={errs.bank_iban?.message}
                >
                  <Input
                    id="bank-iban"
                    placeholder="DE89 …"
                    autoComplete="off"
                    spellCheck={false}
                    className={`font-mono uppercase ${fieldClass(!!errs.bank_iban)}`}
                    {...reg("bank_iban")}
                  />
                </Field>

                <Field label={t("admin.field.bankBic", "BIC")}>
                  <Input
                    id="bank-bic"
                    autoComplete="off"
                    spellCheck={false}
                    className="font-mono uppercase"
                    {...reg("bank_bic")}
                  />
                </Field>

                <Field label={t("admin.field.bankName", "Bank name")}>
                  <Input
                    id="bank-name"
                    placeholder="Commerzbank"
                    {...reg("bank_name")}
                  />
                </Field>
              </div>

              <div className="mt-1 flex items-start justify-between gap-3 rounded-md border bg-background px-3 py-2.5">
                <div className="space-y-0.5">
                  <Label htmlFor="is-platform" className="text-xs font-medium">
                    {t(
                      "admin.field.isPlatform",
                      "This is the coliviq (platform) entity"
                    )}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "admin.field.isPlatformDesc",
                      "Use this bank account as the default coliviq account that collects management fees. Only one owner can be the platform entity."
                    )}
                  </p>
                </div>
                <Switch
                  id="is-platform"
                  checked={form.watch("is_platform")}
                  onCheckedChange={(v) => form.setValue("is_platform", v)}
                />
              </div>
            </Section>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t("admin.action.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("admin.action.saving", "Saving…")
                  : mode === "create"
                    ? t("admin.owner.addAction", "Add owner")
                    : t("admin.action.saveChanges", "Save changes")}
              </Button>
            </DialogFooter>
            <p className="text-[11px] text-muted-foreground text-right">
              {t("admin.tip.cmdEnter", "Tip: press ⌘ ↵ to save")}
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Why the payout details are being removed. A changed IBAN explains
        itself on the timeline — one account replaced by another. A cleared
        one says only that money can no longer be paid out, so the database
        refuses it without a reason and records the reason beside it. */}
      <AlertDialog
        open={!!bankReason}
        onOpenChange={(o) => {
          if (!o && !isSubmitting) setBankReason(null);
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.lessor.removingPayoutDetails",
                "Removing payout details"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {`${t("admin.lessor.removingPayoutDetailsDescPrefix", "This clears bank details")}${lessor?.name ? ` ${t("admin.lessor.removingPayoutDetailsDescFor", "for")} ${lessor.name} ` : " "}${t("admin.lessor.removingPayoutDetailsDescSuffix", "so coliviq can no longer pay out to that account. Say why — it is recorded on this owner's Activity tab next to the removal.")}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            autoFocus
            rows={3}
            maxLength={500}
            value={bankReason?.text ?? ""}
            onChange={(e) =>
              setBankReason((b) => (b ? { ...b, text: e.target.value } : b))
            }
            placeholder={t(
              "admin.lessor.removePayoutReasonPlaceholder",
              "e.g. Owner offboarded — payouts moved to the new managing agent."
            )}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t("admin.action.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (!bankReason?.text.trim()) return;
                onSubmit(bankReason.input, bankReason.text.trim());
              }}
              // The database refuses an empty reason too; disabling here means
              // the admin is told before the round-trip rather than after.
              disabled={!bankReason?.text.trim() || isSubmitting}
            >
              {isSubmitting
                ? t("admin.action.saving", "Saving…")
                : t("admin.lessor.removeAndRecord", "Remove and record")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Local layout helpers ────────────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border bg-card px-5 py-4 space-y-3 shadow-sm">
      <header className="space-y-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </header>
      {children}
    </section>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <Label className="flex items-center gap-1 text-xs font-medium">
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!error && hint && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
